/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { Timestamp } from '@amazon/vinyl-util'

declare global {
    interface Window {
        amznMusic?: {
            /** The boot config rendered into the served HTML (see {@link readInlineBootConfig}). */
            appConfig?: unknown
            /** Epoch ms the server minted `appConfig`, stamped at render time. */
            appConfigIssuedAt?: unknown
            // GreenHornet marks a cookie-auth build by setting this before boot.
            isCookieAuthEnabled?: boolean
        }
    }
}

/**
 * The CSRF triple the server mints alongside the session; sent back on
 * authenticated requests as `csrf-rnd`/`csrf-token`/`csrf-ts` headers (matches
 * GreenHornet's config.json shape and media-gql-client's CSRFTokenConfig).
 */
export interface CsrfConfig {
    rnd: string
    token: string
    ts: string
}

export interface TokenConfig {
    accessToken: string
    expiresAt: Timestamp
}

/** AuthPortal's `/config.json` body, narrowed to the fields amp reads. */
export interface BootConfig {
    accessToken?: string
    accessTokenExpiresIn?: number
    csrf?: CsrfConfig
    customerId?: string
    isProfileCustomer?: boolean
    montanaCsrf?: string
}

/** A boot config resolved onto absolute time, ready to commit to the auth controller. */
export interface BootSession {
    csrf: CsrfConfig | null
    customerId: string | null
    montanaCsrf: string | null
    /** The account has no listener profile chosen yet, so AuthPortal withholds its token. */
    needsProfilePicker: boolean
    token: TokenConfig | null
}

/** Never hand a token this close to lapsing downstream — it pushes bad auth to the wire. */
export const EXPIRY_BUFFER_MS = 30 * 1000

// An inline config older than this is a stale document (a cache that ignored
// `no-store`, a restored session), so its session claims are refetched.
const MAX_INLINE_AGE_SECONDS = 5 * 60

/** Resolves a boot config's relative token expiry against the time the server minted it. */
export function toBootSession(config: BootConfig, issuedAt: Timestamp): BootSession {
    const expiresIn = config.accessTokenExpiresIn
    return {
        csrf: config.csrf ?? null,
        customerId: nonEmpty(config.customerId),
        montanaCsrf: nonEmpty(config.montanaCsrf),
        needsProfilePicker: Boolean(config.customerId) && config.isProfileCustomer === false,
        token:
            config.accessToken && expiresIn != null
                ? { accessToken: config.accessToken, expiresAt: issuedAt + expiresIn * 1000 }
                : null,
    }
}

/**
 * The boot session the AuthPortal origin rendered into the served HTML as
 * `window.amznMusic.appConfig` (GreenHornet's global), or `null` when the
 * document carries none, carries a malformed one, or is stale enough that its
 * session claims can't be trusted — the caller then POSTs `/config.json` itself.
 *
 * Resolving from the document is what keeps the first paint off the network: the
 * HTML server already knows the session, so the client needn't ask again.
 */
export function readInlineBootConfig(): BootSession | null {
    const { appConfig, appConfigIssuedAt: issuedAt } = window.amznMusic ?? {}
    if (!isRecord(appConfig) || typeof issuedAt !== 'number') return null
    const now = Date.now()
    if (!Number.isFinite(issuedAt) || now - issuedAt > MAX_INLINE_AGE_SECONDS * 1000) return null
    const config = parseBootConfig(appConfig)
    if (!config) return null
    const session = toBootSession(config, issuedAt)
    if (session.token && session.token.expiresAt < now + EXPIRY_BUFFER_MS) return null
    return session
}

// Any field of the wrong type makes the whole payload untrusted: a half-read
// session would boot with a customer id but no csrf, or a token with no expiry.
function parseBootConfig(config: Record<string, unknown>): BootConfig | null {
    const { accessToken, accessTokenExpiresIn, csrf, customerId, isProfileCustomer, montanaCsrf } =
        config
    if (!isOptionalString(customerId) || !isOptionalString(montanaCsrf)) return null
    if (!isOptionalString(accessToken) || !isOptionalBoolean(isProfileCustomer)) return null
    if (!isOptionalCsrfConfig(csrf)) return null
    const expiresIn = toPositiveNumber(accessTokenExpiresIn)
    if (accessToken && expiresIn == null) return null
    return {
        ...(customerId != null ? { customerId } : null),
        ...(montanaCsrf != null ? { montanaCsrf } : null),
        ...(isProfileCustomer != null ? { isProfileCustomer } : null),
        ...(csrf != null ? { csrf } : null),
        ...(accessToken && expiresIn != null
            ? { accessToken, accessTokenExpiresIn: expiresIn }
            : null),
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isOptionalString(value: unknown): value is string | undefined {
    return value == null || typeof value === 'string'
}

function isOptionalBoolean(value: unknown): value is boolean | undefined {
    return value == null || typeof value === 'boolean'
}

function isOptionalCsrfConfig(value: unknown): value is CsrfConfig | undefined {
    if (value == null) return true
    return (
        isRecord(value) &&
        typeof value.rnd === 'string' &&
        typeof value.token === 'string' &&
        typeof value.ts === 'string'
    )
}

// AuthPortal reports the lifetime in seconds; a server rendering it into the
// document may carry it as a string.
function toPositiveNumber(value: unknown): number | null {
    if (typeof value !== 'number' && typeof value !== 'string') return null
    const seconds = Number(value)
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null
}

function nonEmpty(value: string | undefined): string | null {
    return value ? value : null
}
